import React from "react";
import PropTypes from "prop-types";
import { isUndefined, isEmpty, result } from "lodash";
import classNames from "classnames";

const renderLabel = (primaryText) => {
  if (!Array.isArray(primaryText)) return <span>{primaryText}</span>;
  return (
    <span>
      {primaryText.map((line, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {line}
        </React.Fragment>
      ))}
    </span>
  );
};

const locationActive = (routerIsActive, locationPathname, value) => {
  if (typeof routerIsActive !== "function") return false;
  if (typeof locationPathname !== "string") return false;
  if (typeof value !== "string") return false;

  return (
    routerIsActive(value) ||
    (locationPathname.startsWith(value) && value !== "/")
  );
};

const TopNav = ({ items, router, location, onNavigate }) => {
  if (!Array.isArray(items) || !router || !location) {
    return null;
  }

  const topLevelItems = items
    .filter((i) => isUndefined(i.parent))
    .filter((i) => result(i, "conditionFn", true));

  return (
    <nav className="topNav" role="navigation">
      <div className="topNav__inner">
        {topLevelItems.map((item) => {
          const children = items
            .filter((c) => c.parent === item.id)
            .filter((c) => result(c, "conditionFn", true));

          const isActive = locationActive(
            router.isActive,
            location.pathname,
            item.path,
          );

          const hasChildren = !isEmpty(children);

          // Link item (external)
          if (!isUndefined(item.link)) {
            return (
              <a
                key={item.id}
                className={classNames("topNav__item", {
                  "is-active": isActive,
                })}
                href={item.link}
                target={item.target}
                rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
              >
                {renderLabel(item.primaryText)}
                {item.srHint && (
                  <span className="visually-hidden">{item.srHint}</span>
                )}
              </a>
            );
          }

          // Single route item
          if (!hasChildren) {
            return (
              <button
                key={item.id}
                type="button"
                className={classNames("topNav__item", {
                  "is-active": isActive,
                })}
                onClick={() => onNavigate(item)}
              >
                {renderLabel(item.primaryText)}
              </button>
            );
          }

          // Dropdown - purely UI; logic stays the same (navigate to child routes)
          return (
            <div
              key={item.id}
              className={classNames("topNav__dropdown", {
                "is-active": isActive,
              })}
            >
              <button
                type="button"
                className={classNames("topNav__item", {
                  "is-active": isActive,
                })}
              >
                {renderLabel(item.primaryText)}
              </button>
              <div className="topNav__dropdownMenu">
                {children.map((child) => {
                  const childActive = locationActive(
                    router.isActive,
                    location.pathname,
                    child.path,
                  );
                  return (
                    <button
                      key={child.id}
                      type="button"
                      className={classNames("topNav__dropdownItem", {
                        "is-active": childActive,
                      })}
                      onClick={() => onNavigate(child)}
                    >
                      {renderLabel(child.primaryText)}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </nav>
  );
};

TopNav.propTypes = {
  items: PropTypes.array.isRequired,
  router: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  onNavigate: PropTypes.func.isRequired,
};

export default TopNav;
